import { useEffect, useRef, useState } from 'react'

// Formatos de código de barras comunes en tiendas
const FORMATOS_BARRAS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf']

export default function EscanerCamera({ onCodigo, onCerrar }) {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const detectorRef = useRef(null)
  const scanLoopRef = useRef(null)
  const ultimoRef = useRef('')
  const activoRef = useRef(true)

  const [estado, setEstado] = useState('iniciando') // iniciando | activo | error | no-soportado
  const [msgError, setMsgError] = useState('')

  useEffect(() => {
    iniciarEscaneo()
    return detener
  }, [])

  async function iniciarEscaneo() {
    // 1. Verificar acceso a cámara (requiere HTTPS o localhost)
    if (!navigator.mediaDevices?.getUserMedia) {
      setEstado('error')
      setMsgError('Tu navegador no soporta acceso a la cámara. Usa Chrome o Safari actualizado.')
      return
    }

    // 2. Iniciar stream de cámara
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      })
    } catch (e) {
      setEstado('error')
      if (e.name === 'NotAllowedError') {
        setMsgError('Permiso de cámara denegado. Ve a Configuración del navegador → Permisos → Cámara y permite el acceso.')
      } else if (e.name === 'NotFoundError') {
        setMsgError('No se encontró ninguna cámara en este dispositivo.')
      } else {
        setMsgError(`No se pudo acceder a la cámara (${e.name}). Asegúrate de usar HTTPS.`)
      }
      return
    }

    streamRef.current = stream
    if (videoRef.current) {
      videoRef.current.srcObject = stream
      await videoRef.current.play()
    }

    // 3. Intentar BarcodeDetector (nativo en Chrome/Android — sin librería)
    if ('BarcodeDetector' in window) {
      try {
        const formatos = await BarcodeDetector.getSupportedFormats()
        const usados = FORMATOS_BARRAS.filter(f => formatos.includes(f))
        detectorRef.current = new BarcodeDetector({ formats: usados.length ? usados : ['ean_13'] })
        setEstado('activo')
        iniciarLoop()
        return
      } catch (e) {
        // BarcodeDetector existe pero falla → usar fallback
      }
    }

    // 4. Fallback: @zxing/browser
    try {
      const { BrowserMultiFormatReader } = await import('@zxing/browser')
      const reader = new BrowserMultiFormatReader()
      setEstado('activo')
      // @zxing maneja su propio loop internamente
      reader.decodeFromStream(stream, videoRef.current, (result) => {
        if (result && activoRef.current) procesarCodigo(result.getText())
      }).catch(() => {})
      detectorRef.current = { esZxing: true, reader }
    } catch (e) {
      setEstado('error')
      setMsgError('No se pudo inicializar el lector. Intenta actualizar Chrome.')
    }
  }

  function iniciarLoop() {
    const canvas = canvasRef.current
    const video = videoRef.current
    if (!canvas || !video || !detectorRef.current) return

    async function frame() {
      if (!activoRef.current) return
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        try {
          const resultados = await detectorRef.current.detect(canvas)
          if (activoRef.current && resultados?.length > 0) {
            procesarCodigo(resultados[0].rawValue)
          }
        } catch (_) {}
      }
      if (activoRef.current) {
        scanLoopRef.current = requestAnimationFrame(frame)
      }
    }
    scanLoopRef.current = requestAnimationFrame(frame)
  }

  function procesarCodigo(codigo) {
    if (!codigo || !activoRef.current || codigo === ultimoRef.current) return
    ultimoRef.current = codigo
    activoRef.current = false
    onCodigo(codigo)
  }

  function detener() {
    activoRef.current = false
    cancelAnimationFrame(scanLoopRef.current)
    streamRef.current?.getTracks().forEach(t => t.stop())
    if (detectorRef.current?.esZxing) {
      detectorRef.current.reader?.reset?.()
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Barra superior */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
        <div>
          <p className="text-white font-bold text-lg">Escanear código</p>
          <p className="text-white/60 text-sm">
            {estado === 'activo' ? 'Apunta al código de barras' : 'Iniciando cámara...'}
          </p>
        </div>
        <button
          onClick={onCerrar}
          className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white active:scale-90"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Video */}
      <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
      <canvas ref={canvasRef} className="hidden" />

      {/* Visor cuando está activo */}
      {estado === 'activo' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-72 h-48">
            {[
              'top-0 left-0 border-r-0 border-b-0 rounded-tl-xl',
              'top-0 right-0 border-l-0 border-b-0 rounded-tr-xl',
              'bottom-0 left-0 border-r-0 border-t-0 rounded-bl-xl',
              'bottom-0 right-0 border-l-0 border-t-0 rounded-br-xl',
            ].map((cls, i) => (
              <div key={i} className={`absolute ${cls} w-10 h-10 border-4 border-tienda-accent`} />
            ))}
            <div className="absolute left-2 right-2 h-0.5 bg-tienda-accent animate-scan rounded-full shadow-lg" />
          </div>
          <p className="absolute bottom-24 text-white/80 text-sm text-center px-8">
            El producto se agrega automáticamente al detectar el código
          </p>
        </div>
      )}

      {/* Spinner iniciando */}
      {estado === 'iniciando' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="w-12 h-12 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error */}
      {estado === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center p-6 bg-black/70">
          <div className="bg-tienda-surface rounded-2xl p-6 text-center space-y-4 max-w-sm">
            <p className="text-5xl">📷</p>
            <p className="text-tienda-text font-semibold leading-snug">{msgError}</p>
            <p className="text-tienda-muted text-sm">
              También puedes usar un lector USB o Bluetooth — se detecta automáticamente.
            </p>
            <button onClick={onCerrar} className="btn-ghost w-full text-center">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  )
}
